import { URL } from "api";
import { useUploadAnexoStore } from "./_state";

/**
 * Resultado do upload de um anexo
 */
interface UploadResult {
  seq: string;
  status: "sucesso" | "erro";
  result?: any;
  error?: string;
  statusCode?: number;
}

/**
 * Faz upload de todos os anexos cadastrados para a API
 * @param rec - REC da pré-nota
 * @returns Array com os resultados de cada upload
 */
export async function uploadAnexos(
  rec: string,
  tipo?: string
): Promise<UploadResult[]> {
  const uploads = useUploadAnexoStore.getState().uploads;

  if (!uploads || uploads.length === 0) {
    // Não exibe toast de erro - agora só chamamos quando há anexos
    return [];
  }

  const results: UploadResult[] = [];

  for (const upload of uploads) {
    const { seq, file } = upload;

    if (!file) {
      const error = `Arquivo não encontrado para seq ${seq}`;
      console.error(`❌ [UPLOAD] ${error}`);
      // Não exibe toast de erro para não poluir
      results.push({ seq, status: "erro", error });
      continue;
    }

    const result = await uploadSingleFile(file, seq, rec, tipo);
    results.push(result);
  }

  return results;
}

/**
 * Faz upload de um arquivo individual
 * @param file - Arquivo a ser enviado
 * @param seq - Sequência do anexo
 * @param rec - REC da pré-nota
 * @returns Resultado do upload
 *
 * @description Usa a API real do servidor de arquivos (http://172.16.99.187:3001/upload)
 */
async function uploadSingleFile(
  file: File,
  seq: string,
  rec: string,
  tipo?: string
): Promise<UploadResult> {
  // Prepara FormData
  const formData = new FormData();
  formData.append("doc", rec);
  formData.append("seq", seq);
  formData.append("file", file, file.name);

  try {
    // Usa a API real do servidor de arquivos via proxy interno
    const response = await fetch(URL.in.API_LOCAL_APPS_UPLOAD, {
      method: "POST",
      body: formData,
      headers: {
        tipo: tipo || "",
      },
      // Não precisa definir Content-Type - o browser define automaticamente para FormData
    });

    if (!response.ok) {
      const error = new Error(
        `Erro na API: ${response.status} ${response.statusText}`
      ) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    // Tenta fazer parse da resposta como JSON, mas trata caso não seja JSON válido
    let result;
    try {
      const responseText = await response.text();

      if (responseText.trim()) {
        result = JSON.parse(responseText);
      } else {
        // Resposta vazia - considera sucesso
        result = { success: true, message: "Upload realizado com sucesso" };
      }
    } catch {
      result = { success: true, message: "Upload realizado com sucesso" };
    }

    return {
      seq,
      status: "sucesso",
      result: result,
      statusCode: 200,
    };
  } catch (err: any) {
    const errorMessage = extractErrorMessage(err);
    console.error(`❌ [UPLOAD] Erro seq ${seq}:`, errorMessage);

    return {
      seq,
      status: "erro",
      error: errorMessage,
      statusCode: typeof err?.status === "number" ? err.status : undefined,
    };
  }
}

/**
 * Extrai mensagem de erro da resposta da API
 * @param err - Erro da requisição
 * @returns Mensagem de erro formatada
 */
function extractErrorMessage(err: any): string {
  // Para erros da API de arquivos
  if (err.message) {
    return err.message;
  }

  // Para erros da API externa (fallback)
  if (err.response?.data) {
    return typeof err.response.data === "string"
      ? err.response.data
      : JSON.stringify(err.response.data);
  }

  return "Erro desconhecido";
}
